import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { Calendar } from "../ui/calendar";
import { SERVICES, BOOKING_IMG } from "../../data";
import { useLang } from "../../i18n";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingSystem = () => {
  const { t } = useLang();
  const b = t.booking;
  const [date, setDate] = useState();
  const [service, setService] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [unavailable, setUnavailable] = useState([]);

  useEffect(() => {
    axios.get(`${API}/unavailable-dates`).then(({ data }) => setUnavailable(data.dates || [])).catch(() => {});
  }, []);

  const isBlocked = (d) => {
    const past = d < new Date(new Date().setHours(0, 0, 0, 0));
    return past || unavailable.includes(format(d, "yyyy-MM-dd"));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error(b.errDate);
    if (!service) return toast.error(b.errService);
    if (!form.name || !form.email) return toast.error(b.errName);
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, service, date: format(date, "yyyy-MM-dd") });
      setDone(true);
      toast.success(b.ok);
    } catch (err) {
      toast.error(err?.response?.data?.detail || b.fail);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#2D2A26]/25 py-3 text-lg font-body placeholder:text-[#2D2A26]/40 focus:border-[#E2725B] focus:outline-none transition-colors";

  return (
    <section id="book" data-testid="booking-section" className="px-6 md:px-12 py-28 md:py-40">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="overline text-[#E2725B] mb-6">{b.overline}</p>
          <h2 className="font-serif-display text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] mb-8">
            {b.t1}<br /><span className="italic text-[#E2725B]">{b.t2}</span>.
          </h2>
          <p className="text-[#2D2A26]/70 text-base md:text-lg max-w-md leading-relaxed mb-10">{b.desc}</p>
          <div className="overflow-hidden aspect-[16/10] rounded-sm hidden lg:block max-w-md">
            <img src={BOOKING_IMG} alt="Session preview" className="w-full h-full object-cover" />
          </div>
        </div>

        {done ? (
          <motion.div data-testid="booking-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F1EBE1] rounded-sm p-10 md:p-16 flex flex-col items-start">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#E2725B] text-[#F9F6F0] mb-8"><Check className="w-8 h-8" strokeWidth={1.5} /></span>
            <h3 className="font-serif-display text-4xl md:text-5xl font-light mb-4">{b.successTitle}</h3>
            <p className="text-[#2D2A26]/70 text-lg max-w-sm leading-relaxed">
              {b.successBody(form.name.split(" ")[0], service, date && format(date, "MMMM do, yyyy"), form.email)}
            </p>
            <button data-testid="booking-reset" onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "" }); setService(""); setDate(undefined); }}
              className="mt-10 overline text-[0.66rem] border-b border-[#2D2A26]/40 pb-1 hover:text-[#E2725B] hover:border-[#E2725B] transition-colors">
              {b.again}
            </button>
          </motion.div>
        ) : (
          <form data-testid="booking-form" onSubmit={submit} className="space-y-10">
            <div>
              <label className="overline text-[0.6rem] text-[#2D2A26]/60 block mb-4">{b.step1}</label>
              <div className="flex flex-wrap gap-2.5">
                {SERVICES.map((s) => (
                  <button type="button" key={s} data-testid={`service-pick-${s.split(" ")[0].toLowerCase()}`} onClick={() => setService(s)}
                    className={`px-4 py-2 rounded-full text-sm border transition-all duration-300 ${service === s ? "bg-[#2D2A26] text-[#F9F6F0] border-[#2D2A26]" : "border-[#2D2A26]/25 text-[#2D2A26]/70 hover:border-[#E2725B] hover:text-[#E2725B]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="overline text-[0.6rem] text-[#2D2A26]/60 block mb-4">{b.step2}</label>
              <div className="inline-block bg-[#F1EBE1] rounded-sm p-3" data-testid="booking-calendar">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={isBlocked} className="rounded-sm" />
              </div>
              {unavailable.length > 0 && (
                <p className="text-[0.7rem] text-[#2D2A26]/45 mt-2" data-testid="unavailable-note">Greyed-out dates are already booked.</p>
              )}
            </div>

            <div>
              <label className="overline text-[0.6rem] text-[#2D2A26]/60 block mb-4">{b.step3}</label>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="booking-name" className={inputCls} placeholder={b.name} value={form.name} onChange={set("name")} />
                <input data-testid="booking-email" type="email" className={inputCls} placeholder={b.email} value={form.email} onChange={set("email")} />
                <input data-testid="booking-phone" className={`${inputCls} sm:col-span-2`} placeholder={b.phone} value={form.phone} onChange={set("phone")} />
                <textarea data-testid="booking-message" rows={3} className={`${inputCls} sm:col-span-2 resize-none`} placeholder={b.msg} value={form.message} onChange={set("message")} />
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="booking-company" className="hidden" value={form.company} onChange={set("company")} />
              </div>
            </div>

            <button data-testid="booking-submit" type="submit" disabled={loading}
              className="group inline-flex items-center gap-4 px-9 py-4 bg-[#2D2A26] text-[#F9F6F0] rounded-full overline text-[0.66rem] hover:bg-[#E2725B] transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? b.sending : b.submit}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
